/* Global layer of the Inkhunt design language (apps/web/DESIGN.md). Token values are emitted as
   --ih-* custom properties by `tokenStyles` in theme.ts, which follows prefers-color-scheme;
   everything here only consumes them.

   Responsive breakpoints mirror MUI's defaults so raw media queries here and
   `sx={{ xs: …, sm: … }}` objects in components describe the same widths:

     phone           < 600px   @media (max-width: 599.95px)  theme.breakpoints.down("sm")
     phone + tablet  < 900px   @media (max-width: 899.95px)  theme.breakpoints.down("md")

   Prefer clamp() for type and spacing; reserve media queries for changes of layout axis
   (row -> column) and for hiding elements. */

*,
*::before,
*::after {
  box-sizing: border-box;
}

html {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  -webkit-text-size-adjust: 100%;
  scroll-padding-top: calc(var(--ih-nav-h) + 16px);
}

body {
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  background-color: var(--ih-canvas);
  color: var(--ih-ink);
  font-family: var(--ih-font);
  font-size: var(--ih-body-size);
  line-height: var(--ih-body-leading);
  letter-spacing: var(--ih-body-tracking);
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  -webkit-tap-highlight-color: transparent;
  caret-color: var(--ih-ink);
  scrollbar-color: var(--ih-fill-strong) transparent;
}

::selection {
  background-color: var(--ih-selection);
  color: var(--ih-ink);
}

code {
  font-family: ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace;
}

img,
video {
  max-width: 100%;
}

h1,
h2,
h3,
h4 {
  text-wrap: balance;
}

p {
  text-wrap: pretty;
}

/* App-wide link colour: ink, never the accent. MUI's own Link and component classes still
   override it on specificity. */
a {
  color: var(--ih-ink);
  text-decoration: none;
  text-underline-offset: 0.2em;
  text-decoration-thickness: 1px;
}

a:visited {
  color: var(--ih-ink);
}

a:hover {
  text-decoration: underline;
  text-decoration-color: var(--ih-line-strong);
}

/* One focus ring everywhere a keyboard can land. It follows each element's own radius (pills
   stay pills, avatars stay round). MUI ButtonBase draws the same ring through
   .Mui-focusVisible (theme.ts). */
:where(a, button, [role="button"], [tabindex]):focus-visible {
  outline: 2px solid var(--ih-focus);
  outline-offset: 2px;
}

input,
select,
textarea,
button {
  font: inherit;
  letter-spacing: inherit;
}

/* No double-tap-zoom delay on anything tappable. */
a,
button,
[role="button"],
label {
  touch-action: manipulation;
}

/* Page scaffolding shared by every signed-in page: clears the fixed nav, centres the content
   column and lets the page rise in (transitionConstants.ts). */
.ih-page {
  min-height: 100dvh;
  padding-top: calc(var(--ih-nav-h) + clamp(24px, 4vw, 48px));
  padding-bottom: var(--ih-space-section);
}

.ih-container {
  width: 100%;
  max-width: calc(var(--ih-content-max) + 2 * var(--ih-gutter));
  margin-inline: auto;
  padding-inline: var(--ih-gutter);
}

/* Empty and error states: quiet, centred, in the headline voice. */
.ih-empty {
  margin: clamp(48px, 10vh, 96px) auto;
  max-width: var(--ih-text-measure);
  padding-inline: var(--ih-gutter);
  text-align: center;
  color: var(--ih-ink-secondary);
  font-size: var(--ih-headline-size);
  line-height: var(--ih-headline-leading);
  font-weight: var(--ih-headline-weight);
  letter-spacing: var(--ih-headline-tracking);
}

/* Route-level Suspense fallback (MyRoutes.tsx). */
.ih-route-loading {
  min-height: 100dvh;
  display: grid;
  place-items: center;
  color: var(--ih-ink-secondary);
  font-size: var(--ih-caption-size);
}

/* ErrorBoundary renders outside the MUI ThemeProvider, so it is styled here. */
.ih-crash {
  min-height: 100dvh;
  display: grid;
  place-content: center;
  justify-items: center;
  gap: var(--ih-space-lg);
  padding: var(--ih-gutter);
  text-align: center;
}

.ih-crash h1 {
  margin: 0;
  font-size: var(--ih-title-size);
  line-height: var(--ih-title-leading);
  font-weight: var(--ih-title-weight);
  letter-spacing: var(--ih-title-tracking);
}

.ih-crash button {
  min-height: 48px;
  padding: 0 26px;
  border: 0;
  border-radius: var(--ih-radius-pill);
  background: var(--ih-action);
  color: var(--ih-on-action);
  font-weight: 500;
  cursor: pointer;
}

/* Visually hidden but announced (labels for icon-only controls). */
.ih-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
